import { useAuthorizationWrapper } from './useAuthorizationWrapper';
import { PrivilegeName, PRIVILEGES, ROLES } from '../api/types/dtos';

/**
 * Hook for checking if user has a specific privilege
 */
export const useHasPrivilege = (privilege: PrivilegeName | string) => {
  const { hasPrivilege, loading, error } = useAuthorizationWrapper();
  return {
    hasPrivilege: hasPrivilege(privilege),
    loading,
    error,
  };
};

/**
 * Hook for checking if user has any of the specified privileges
 */
export const useHasAnyPrivilege = (privileges: PrivilegeName[] | string[]) => {
  const { hasAnyPrivilege, loading } = useAuthorizationWrapper();
  return {
    hasAnyPrivilege: hasAnyPrivilege(privileges),
    loading,
  };
};

/**
 * Hook for checking if user has all of the specified privileges
 */
export const useHasAllPrivileges = (privileges: PrivilegeName[] | string[]) => {
  const { hasAllPrivileges, loading } = useAuthorizationWrapper();
  return {
    hasAllPrivileges: hasAllPrivileges(privileges),
    loading,
  };
};

/**
 * Hook for checking if user has a specific role
 */
export const useHasRole = (roleName: string) => {
  const { hasRole, loading } = useAuthorizationWrapper();
  return {
    hasRole: hasRole(roleName),
    loading,
  };
};

/**
 * Hook for checking if user has any of the specified roles
 */
export const useHasAnyRole = (roleNames: string[]) => {
  const { hasAnyRole, loading } = useAuthorizationWrapper();
  return {
    hasAnyRole: hasAnyRole(roleNames),
    loading,
  };
};

/**
 * Hook for checking admin privileges
 */
export const useIsAdmin = () => {
  const { hasPrivilege, hasRole, loading } = useAuthorizationWrapper();
  return {
    isAdmin: hasPrivilege(PRIVILEGES.ADMIN_ACCESS) || hasRole(ROLES.ADMIN),
    loading,
  };
};

/**
 * Hook for checking super admin privileges
 */
export const useIsSuperAdmin = () => {
  const { hasPrivilege, hasRole, loading } = useAuthorizationWrapper();
  return {
    isSuperAdmin: hasPrivilege(PRIVILEGES.SUPER_ADMIN) || hasRole(ROLES.SUPER_ADMIN),
    loading,
  };
};

/**
 * Hook for checking user management permissions
 */
export const useCanManageUsers = () => {
  const { hasAnyPrivilege, loading } = useAuthorizationWrapper();
  return {
    canManageUsers: hasAnyPrivilege([
      PRIVILEGES.CREATE_USER,
      PRIVILEGES.UPDATE_USER,
      PRIVILEGES.DELETE_USER,
    ]),
    loading,
  };
};

/**
 * Hook for checking organization management permissions
 */
export const useCanManageOrganizations = () => {
  const { hasAnyPrivilege, loading } = useAuthorizationWrapper();
  return {
    canManageOrganizations: hasAnyPrivilege([
      PRIVILEGES.CREATE_ORGANIZATION,
      PRIVILEGES.UPDATE_ORGANIZATION,
      PRIVILEGES.DELETE_ORGANIZATION,
    ]),
    loading,
  };
};

/**
 * Hook for checking inspection management permissions
 */
export const useCanManageInspections = () => {
  const { hasAnyPrivilege, loading } = useAuthorizationWrapper();
  return {
    canManageInspections: hasAnyPrivilege([
      PRIVILEGES.CREATE_INSPECTION,
      PRIVILEGES.UPDATE_INSPECTION,
      PRIVILEGES.DELETE_INSPECTION,
    ]),
    loading,
  };
};



/**
 * Hook for getting all active privileges and roles
 */
export const useAuthorizationInfo = () => {
  const { 
    getActivePrivileges, 
    getActiveRoles, 
    loading, 
    error, 
    lastUpdated 
  } = useAuthorizationWrapper();
  
  return {
    activePrivileges: getActivePrivileges(),
    activeRoles: getActiveRoles(),
    loading,
    error,
    lastUpdated,
  };
};
